// src/pages/admin/Campaigns.tsx - FIXED VERSION
import React, { useState, useEffect } from 'react';
import { 
  Filter, 
  Download, 
  Search, 
  Calendar,
  Users,
  Globe,
  CheckCircle,
  Clock,
  Send,
  Eye,
  TrendingUp,
  DollarSign
} from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

const AdminCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    aspirant: 'all',
    search: '',
  });

  useEffect(() => {
    fetchCampaigns();
  }, [filters.status, filters.type]);

  const fetchCampaigns = async () => {
    try {
      console.log('📡 Fetching campaigns...');
      
      // Try to fetch from API, but handle if endpoint doesn't exist
      let response;
      try {
        response = await apiService.getCampaigns();
        console.log('✅ Campaigns API response:', response);
        console.log('📊 Response data structure:', response.data);
      } catch (apiError) {
        console.warn('⚠️ Campaigns endpoint not available, using mock data');
        // Use mock data if API call fails
        response = {
          data: [
            {
              id: 1,
              campaignName: 'Community Health Outreach',
              status: 'DRAFT',
              type: 'manifesto',
              cost: 15000,
              estimatedRecipients: 5000,
              language: 'English',
              aspirantName: 'John Doe',
              description: 'Health awareness campaign for the community focusing on preventive care',
              createdAt: new Date().toISOString()
            },
            {
              id: 2,
              campaignName: 'Road Repair Initiative',
              status: 'PUBLISHED',
              type: 'policy',
              cost: 25000,
              estimatedRecipients: 8000,
              language: 'Swahili',
              aspirantName: 'Jane Smith',
              description: 'Campaign advocating for road repairs and infrastructure development',
              createdAt: new Date().toISOString()
            },
            {
              id: 3,
              campaignName: 'Youth Employment Program',
              status: 'COMPLETED',
              type: 'announcement',
              cost: 18000,
              estimatedRecipients: 3000,
              language: 'English',
              aspirantName: 'Mike Johnson',
              description: 'Program to connect youth with employment opportunities',
              createdAt: new Date().toISOString()
            },
            {
              id: 4,
              campaignName: 'Water Access Project',
              status: 'SCHEDULED',
              type: 'event',
              cost: 32000,
              estimatedRecipients: 10000,
              language: 'Swahili',
              aspirantName: 'Sarah Williams',
              description: 'Project to improve water access in rural areas',
              createdAt: new Date().toISOString()
            },
            {
              id: 5,
              campaignName: 'Education Scholarship Fund',
              status: 'SMS_SENT',
              type: 'policy',
              cost: 12000,
              estimatedRecipients: 4000,
              language: 'English',
              aspirantName: 'David Brown',
              description: 'Scholarship program for underprivileged students',
              createdAt: new Date().toISOString()
            },
            {
              id: 6,
              campaignName: 'Security Awareness',
              status: 'DRAFT',
              type: 'announcement',
              cost: 8000,
              estimatedRecipients: 6000,
              language: 'Swahili',
              aspirantName: 'Emma Wilson',
              description: 'Community security and safety awareness campaign',
              createdAt: new Date().toISOString()
            }
          ]
        };
      }
      
      // 🛠️ FIX: Handle different response structures properly
      let campaignsData = [];
      
      if (Array.isArray(response.data)) {
        // If response.data is already an array
        campaignsData = response.data;
        console.log('✅ Direct array response');
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        // If response.data has a data property that's an array
        campaignsData = response.data.data;
        console.log('✅ Nested array in response.data.data');
      } else if (typeof response.data === 'object' && response.data !== null) {
        // Try to extract array from object
        console.log('🔄 Response.data is an object, attempting to extract array...');
        
        // Check common property names
        const dataObj = response.data as any;
        if (Array.isArray(dataObj.campaigns)) {
          campaignsData = dataObj.campaigns;
        } else if (Array.isArray(dataObj.items)) {
          campaignsData = dataObj.items;
        } else if (Array.isArray(dataObj.results)) {
          campaignsData = dataObj.results;
        } else {
          // Try to convert object values to array
          const values = Object.values(dataObj);
          campaignsData = values.filter(item => 
            typeof item === 'object' && 
            item !== null && 
            ((item as any).campaignName || (item as any).name || (item as any).id)
);
        }
      }
      
      // Final safety check
      if (!Array.isArray(campaignsData)) {
        console.error('❌ campaignsData is not an array:', campaignsData);
        campaignsData = [];
      }
      
      console.log(`📊 Setting ${campaignsData.length} campaigns`);
      setCampaigns(campaignsData);
      
    } catch (error) {
      console.error('❌ Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns data');
      setCampaigns([]); // Always set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    console.log('Exporting campaigns...');
    toast.success('Export started');
  };

  const handleApproveCampaign = async (campaignId: number) => {
    try {
      // This would call an approve endpoint when available
      toast.success('Campaign approved');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to approve campaign');
    }
  };

  const campaignTypes = [
    { id: 'manifesto', name: 'Manifesto', color: 'bg-blue-500' },
    { id: 'policy', name: 'Policy', color: 'bg-green-500' },
    { id: 'announcement', name: 'Announcement', color: 'bg-purple-500' },
    { id: 'event', name: 'Event', color: 'bg-orange-500' },
  ];

  const statuses = [
    { id: 'draft', name: 'Draft', icon: <Clock size={16} />, color: 'bg-gray-100 text-gray-800' },
    { id: 'scheduled', name: 'Scheduled', icon: <Calendar size={16} />, color: 'bg-yellow-100 text-yellow-800' },
    { id: 'published', name: 'Published', icon: <Send size={16} />, color: 'bg-blue-100 text-blue-800' },
    { id: 'sms_sent', name: 'SMS Sent', icon: <CheckCircle size={16} />, color: 'bg-green-100 text-green-800' },
    { id: 'completed', name: 'Completed', icon: <CheckCircle size={16} />, color: 'bg-purple-100 text-purple-800' },
  ];

  const aspirants = [
    { id: 1, name: 'John Doe', party: 'Jubilee' },
    { id: 2, name: 'Jane Smith', party: 'ODM' },
    { id: 3, name: 'Mike Johnson', party: 'UDA' },
    { id: 4, name: 'Sarah Williams', party: 'Wiper' },
    { id: 5, name: 'David Brown', party: 'Ford Kenya' },
    { id: 6, name: 'Emma Wilson', party: 'ANC' },
  ];

  const getStatusIcon = (status: string) => {
    if (!status) return <Clock className="text-gray-500" size={16} />;
    
    switch (status.toLowerCase()) {
      case 'completed': return <CheckCircle className="text-green-500" size={16} />;
      case 'published': return <Send className="text-blue-500" size={16} />;
      case 'scheduled': return <Calendar className="text-yellow-500" size={16} />;
      case 'sms_sent': return <CheckCircle className="text-green-500" size={16} />;
      default: return <Clock className="text-gray-500" size={16} />;
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'published': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'sms_sent': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Safe filter campaigns based on selected filters
  const getFilteredCampaigns = () => {
    if (!Array.isArray(campaigns)) {
      console.warn('⚠️ Campaigns is not an array:', campaigns);
      return [];
    }
    
    return campaigns.filter(campaign => {
      if (!campaign) return false;
      
      if (filters.status !== 'all' && campaign.status && 
          campaign.status.toLowerCase() !== filters.status) {
        return false;
      }
      if (filters.type !== 'all' && campaign.type !== filters.type) {
        return false;
      }
      if (filters.aspirant !== 'all') {
        const aspirantId = parseInt(filters.aspirant);
        const aspirant = aspirants.find(a => a.id === aspirantId);
        if (aspirant && campaign.aspirantName && 
            !campaign.aspirantName.includes(aspirant.name)) {
          return false;
        }
      }
      if (filters.search && campaign.campaignName && campaign.description) {
        const searchLower = filters.search.toLowerCase();
        const nameMatch = campaign.campaignName.toLowerCase().includes(searchLower);
        const descMatch = campaign.description.toLowerCase().includes(searchLower);
        if (!nameMatch && !descMatch) return false;
      }
      return true;
    });
  };

  const filteredCampaigns = getFilteredCampaigns();

  // Helper function to safely get array length or 0
  const safeArrayLength = (array: any) => {
    return Array.isArray(array) ? array.length : 0;
  };

  // Helper function to safely filter arrays
  const safeFilterCount = (array: any, filterFn: (item: any) => boolean) => {
    if (!Array.isArray(array)) return 0;
    return array.filter(filterFn).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Management</h1>
          <p className="text-gray-600">Monitor and manage aspirant campaigns</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Download size={16} className="mr-2" />
            Export Data
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-start">
          <AlertTriangle className="text-yellow-600 mr-3 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-yellow-800 font-medium">Campaigns Feature</p>
            <p className="text-sm text-yellow-700 mt-1">
              This section is using demonstration data. Connect to backend API when campaigns endpoint is available.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-lg mr-3">
              <Users className="text-blue-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Campaigns</p>
              <p className="text-xl font-bold text-gray-900">{safeArrayLength(campaigns)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="p-2 bg-green-50 rounded-lg mr-3">
              <Send className="text-green-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-xl font-bold text-gray-900">
                {safeFilterCount(campaigns, c => 
                  c && c.status && (c.status === 'PUBLISHED' || c.status === 'SMS_SENT')
                )}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="p-2 bg-purple-50 rounded-lg mr-3">
              <DollarSign className="text-purple-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Spend</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(
                  Array.isArray(campaigns) 
                    ? campaigns.reduce((sum, c) => sum + (c?.cost || 0), 0)
                    : 0
                )}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-orange-500">
          <div className="flex items-center">
            <div className="p-2 bg-orange-50 rounded-lg mr-3">
              <TrendingUp className="text-orange-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg Reach</p>
              <p className="text-xl font-bold text-gray-900">
                {Array.isArray(campaigns) && campaigns.length > 0
                  ? Math.round(
                      campaigns.reduce((sum, c) => sum + (c?.estimatedRecipients || 0), 0) / 
                      campaigns.length
                    ).toLocaleString()
                  : 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="all">All Statuses</option>
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            >
              <option value="all">All Types</option>
              {campaignTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Aspirant</label>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.aspirant}
              onChange={(e) => setFilters({ ...filters, aspirant: e.target.value })}
            >
              <option value="all">All Aspirants</option>
              {aspirants.map((aspirant) => (
                <option key={aspirant.id} value={aspirant.id}>{aspirant.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">&nbsp;</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="search"
                placeholder="Search campaigns..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCampaigns.slice(0, 6).map((campaign) => (
          <div key={campaign?.id || Math.random()} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{campaign?.campaignName || 'Unnamed Campaign'}</h3>
                  <div className="flex items-center mt-2 space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign?.status)}`}>
                      {getStatusIcon(campaign?.status)}
                      <span className="ml-1 capitalize">
                        {campaign?.status ? campaign.status.toLowerCase().replace('_', ' ') : 'Unknown'}
                      </span>
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {campaignTypes.find(t => t.id === campaign?.type)?.name || 
                       campaign?.type?.replace('_', ' ') || 
                       'Manifesto'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(campaign?.cost || 0)}</p>
                  <p className="text-sm text-gray-600">Budget</p>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <Users size={16} className="mr-2" />
                    <span>{(campaign?.actualRecipients || campaign?.estimatedRecipients || 0).toLocaleString()} recipients</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Globe size={16} className="mr-2" />
                    <span>{campaign?.language || 'English'}</span>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {campaign?.aspirantName || 'Unknown Aspirant'}
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {campaign?.description || 'No description available'}
              </p>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-500">
                  Created: {campaign?.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : 'Unknown date'}
                </div>
                <div className="flex space-x-2">
                  <button 
                    className="px-3 py-1 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition-colors"
                    onClick={() => toast('View details feature coming soon')}
                  >
                    <Eye size={16} />
                  </button>
                  {campaign?.status === 'DRAFT' && (
                    <button
                      onClick={() => handleApproveCampaign(campaign.id)}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      Approve
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCampaigns.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <Search className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
          <p className="mt-1 text-sm text-gray-500">Try adjusting your filters or search terms.</p>
        </div>
      )}

      {/* Analytics Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Campaign Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Campaign Types</span>
              <span className="text-sm text-gray-500">Distribution</span>
            </div>
            <div className="space-y-2">
              {campaignTypes.map((type) => {
                const count = safeFilterCount(campaigns, c => c?.type === type.id);
                return (
                  <div key={type.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full ${type.color} mr-2`}></div>
                      <span className="text-sm text-gray-700">{type.name}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Status Overview</span>
              <span className="text-sm text-gray-500">Current</span>
            </div>
            <div className="space-y-2">
              {statuses.map((status) => {
                const count = safeFilterCount(campaigns, c => 
                  c?.status && c.status.toLowerCase() === status.id
                );
                return (
                  <div key={status.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="mr-2">{status.icon}</div>
                      <span className="text-sm text-gray-700">{status.name}</span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Top Aspirants</span>
              <span className="text-sm text-gray-500">By Campaigns</span>
            </div>
            <div className="space-y-2">
              {aspirants.slice(0, 5).map((aspirant) => {
                const count = safeFilterCount(campaigns, c => 
                  c?.aspirantName?.includes(aspirant.name)
                );
                return (
                  <div key={aspirant.id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{aspirant.name}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        {aspirant.party}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {[
            { action: 'New campaign created', user: 'John Doe', time: '2 hours ago' },
            { action: 'Campaign approved', user: 'Admin', time: '4 hours ago' },
            { action: 'SMS campaign sent', user: 'Jane Smith', time: '1 day ago' },
            { action: 'Campaign updated', user: 'Mike Johnson', time: '2 days ago' },
          ].map((activity, index) => (
            <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
              <div>
                <p className="font-medium text-gray-900">{activity.action}</p>
                <p className="text-sm text-gray-500">by {activity.user}</p>
              </div>
              <span className="text-sm text-gray-500">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminCampaigns;

// Helper component for info banner
const AlertTriangle: React.FC<{ className?: string; size?: number }> = ({ className, size = 20 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    className={className}
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
    <path d="M12 9v4"/>
    <path d="M12 17h.01"/>
  </svg>
);